/**
 * Phase 3D: Evidence Viewer Dialog
 * 
 * Shows evidence snippets for a CPT code with text highlighting.
 * Allows clinicians to see exactly what text triggered each match.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  FileText,
  Eye,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EvidenceSnippet, DocumentationStatusV2 } from '@/lib/phase3/types';
import { CptDetectionResult, ElementMatchResult } from '@/lib/reconciliation/detectionEngine';

interface EvidenceViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cptResult: CptDetectionResult | null;
  fullNoteText?: string;
}

function DocumentationStatusBadge({ status }: { status: DocumentationStatusV2 }) {
  const variants: Record<DocumentationStatusV2, { 
    icon: React.ReactNode; 
    className: string; 
    label: string;
  }> = {
    sufficient: {
      icon: <CheckCircle2 className="w-3 h-3" />,
      className: 'bg-success/10 text-success border-success/30',
      label: 'Sufficient',
    },
    insufficient: {
      icon: <XCircle className="w-3 h-3" />,
      className: 'bg-destructive/10 text-destructive border-destructive/30',
      label: 'Insufficient',
    },
    ambiguous: {
      icon: <AlertTriangle className="w-3 h-3" />,
      className: 'bg-warning/10 text-warning border-warning/30',
      label: 'Ambiguous',
    },
    not_found: {
      icon: <Search className="w-3 h-3" />,
      className: 'bg-muted text-muted-foreground',
      label: 'Not Found',
    },
  };

  const variant = variants[status];

  return (
    <Badge variant="outline" className={cn('flex items-center gap-1', variant.className)}>
      {variant.icon}
      {variant.label}
    </Badge>
  );
}

function ElementResult({ element }: { element: ElementMatchResult }) {
  return (
    <div className={cn(
      'p-3 rounded-lg border',
      element.matched 
        ? 'bg-success/5 border-success/30' 
        : 'bg-destructive/5 border-destructive/30'
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {element.matched ? (
            <CheckCircle2 className="w-4 h-4 text-success" />
          ) : (
            <XCircle className="w-4 h-4 text-destructive" />
          )}
          <span className="font-medium text-sm">{element.elementLabel}</span>
        </div>
        <Badge variant="secondary" className="text-xs capitalize">
          {element.strictness}
        </Badge>
      </div>
      
      {element.matched && element.matchedKeywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {element.matchedKeywords.map((keyword, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              <Tag className="w-2 h-2 mr-1" />
              {keyword}
            </Badge>
          ))}
        </div>
      )}
      
      {element.matched && element.snippets.length > 0 && (
        <div className="mt-3 space-y-2">
          {element.snippets.slice(0, 2).map((snippet, idx) => (
            <SnippetCard key={idx} snippet={snippet} />
          ))}
          {element.snippets.length > 2 && (
            <p className="text-xs text-muted-foreground">
              +{element.snippets.length - 2} more matches
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SnippetCard({ snippet }: { snippet: EvidenceSnippet }) {
  return (
    <div className="bg-background/80 rounded border p-2 text-sm">
      <p className="text-muted-foreground leading-relaxed">
        "...{snippet.text}..."
      </p>
    </div>
  );
}

function HighlightedNote({ 
  noteText, 
  snippets 
}: { 
  noteText: string; 
  snippets: EvidenceSnippet[];
}) {
  if (!noteText || snippets.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">
        No evidence snippets to highlight
      </div>
    );
  }

  // Sort snippets by offset
  const sortedSnippets = [...snippets].sort((a, b) => a.offset_start - b.offset_start);
  
  // Build highlighted text segments
  const segments: Array<{ text: string; highlighted: boolean; keywords: string[] }> = [];
  let currentIndex = 0;

  for (const snippet of sortedSnippets) {
    // Add non-highlighted text before this snippet
    if (snippet.offset_start > currentIndex) {
      segments.push({
        text: noteText.slice(currentIndex, snippet.offset_start),
        highlighted: false,
        keywords: [],
      });
    }

    // Add highlighted snippet (avoid overlapping)
    if (snippet.offset_start >= currentIndex) {
      segments.push({
        text: noteText.slice(snippet.offset_start, snippet.offset_end),
        highlighted: true,
        keywords: snippet.matched_keywords,
      });
      currentIndex = snippet.offset_end;
    }
  }

  // Add remaining text
  if (currentIndex < noteText.length) {
    segments.push({
      text: noteText.slice(currentIndex),
      highlighted: false,
      keywords: [],
    });
  }

  return (
    <div className="text-sm leading-relaxed font-mono whitespace-pre-wrap">
      {segments.map((segment, idx) => (
        <span
          key={idx}
          className={cn(
            segment.highlighted && 'bg-warning/30 dark:bg-warning/20 rounded px-0.5'
          )}
          title={segment.highlighted ? `Matched: ${segment.keywords.join(', ')}` : undefined}
        >
          {segment.text}
        </span>
      ))}
    </div>
  );
}

export function EvidenceViewerDialog({
  open,
  onOpenChange,
  cptResult,
  fullNoteText,
}: EvidenceViewerDialogProps) {
  const [activeTab, setActiveTab] = useState('elements');

  if (!cptResult) return null;

  const requiredElements = cptResult.elementResults.filter(e => e.strictness === 'required');
  const recommendedElements = cptResult.elementResults.filter(e => e.strictness === 'recommended');
  const optionalElements = cptResult.elementResults.filter(e => e.strictness === 'optional');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Evidence for CPT {cptResult.cptCode}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {cptResult.cptDescription || 'View matched documentation evidence'}
              </DialogDescription>
            </div>
            <DocumentationStatusBadge status={cptResult.documentationStatus} />
          </div>
        </DialogHeader>

        {/* Summary Alert */}
        <Alert className={cn(
          cptResult.documentationStatus === 'sufficient' && 'border-success/50 bg-success/5',
          cptResult.documentationStatus === 'insufficient' && 'border-destructive/50 bg-destructive/5',
          cptResult.documentationStatus === 'ambiguous' && 'border-warning/50 bg-warning/5',
        )}>
          {cptResult.documentationStatus === 'sufficient' ? (
            <CheckCircle2 className="w-4 h-4 text-success" />
          ) : cptResult.documentationStatus === 'insufficient' ? (
            <XCircle className="w-4 h-4 text-destructive" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-warning" />
          )}
          <AlertTitle>
            {cptResult.matchedElementCount} of {cptResult.totalRequiredElements} Required Elements Matched
          </AlertTitle>
          <AlertDescription>
            {cptResult.documentationStatus === 'sufficient' 
              ? 'All required documentation elements were found in the operative note.'
              : cptResult.missingRequiredElements.length > 0
                ? `Missing: ${cptResult.missingRequiredElements.join(', ')}`
                : 'Review the evidence below to verify documentation completeness.'}
          </AlertDescription>
        </Alert>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="elements" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Element Analysis
            </TabsTrigger>
            <TabsTrigger value="highlighted" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Highlighted Note
            </TabsTrigger>
          </TabsList>

          <TabsContent value="elements" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-6">
                {/* Required Elements */}
                {requiredElements.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">Required</Badge>
                      <span className="text-muted-foreground">
                        ({requiredElements.filter(e => e.matched).length}/{requiredElements.length} matched)
                      </span>
                    </h4>
                    <div className="space-y-3">
                      {requiredElements.map(element => (
                        <ElementResult key={element.elementId} element={element} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommended Elements */}
                {recommendedElements.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">Recommended</Badge>
                      <span className="text-muted-foreground">
                        ({recommendedElements.filter(e => e.matched).length}/{recommendedElements.length} matched)
                      </span>
                    </h4>
                    <div className="space-y-3">
                      {recommendedElements.map(element => (
                        <ElementResult key={element.elementId} element={element} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Optional Elements */}
                {optionalElements.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">Optional</Badge>
                      <span className="text-muted-foreground">
                        ({optionalElements.filter(e => e.matched).length}/{optionalElements.length} matched)
                      </span>
                    </h4>
                    <div className="space-y-3">
                      {optionalElements.map(element => (
                        <ElementResult key={element.elementId} element={element} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="highlighted" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              {fullNoteText ? (
                <HighlightedNote 
                  noteText={fullNoteText} 
                  snippets={cptResult.allEvidenceSnippets} 
                />
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Full note text not available</p>
                  <p className="text-sm">Evidence snippets are shown in the Element Analysis tab</p>
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
